import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from '../../layouts/Navbar';
import Sidebar from '../../layouts/Sidebar';
import Footer from '../../layouts/Footer';
import axios from 'axios';
import { toast } from 'react-toastify';

const UpdateTutors = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        qualification: '',
        expertise: [],
        subjects: '',
        region: '',
        experience: '',
        education_level: [],
        availability: '',
        hourly_rate: '',
        status: 'pending',
        verified: '0',
        payment_status: 'pending',
        registration_paid: '0'
    });
    
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [expertiseOptions, setExpertiseOptions] = useState([
        'BECE', 'SHS/WASSCE', 'CTVET', 'University'
    ]);
    const [educationOptions, setEducationOptions] = useState([
        'High School', 'Diploma', "Bachelor's Degree", "Master's Degree", 'PhD'
    ]);
    const [regionOptions, setRegionOptions] = useState([
        'Greater Accra Region', 'Ashanti Region', 'Western Region', 
        'Central Region', 'Volta Region', 'Eastern Region', 
        'Northern Region', 'Upper East Region', 'Upper West Region',
        'Brong-Ahafo Region', 'Oti Region', 'North East Region',
        'Savannah Region', 'Western North Region', 'Ahafo Region'
    ]);

    // Fetch tutor data
    useEffect(() => {
        const fetchTutorData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(
                    `https://pastq.eddcodesgh.com/qApi/admin/tutors/_tutors.php?id=${id}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506`
                        },
                        withCredentials: true
                    }
                );

                if (response.data.status === 'success' && response.data.tutor) {
                    const tutor = response.data.tutor;
                    
                    // Parse JSON fields if they're stored as strings
                    let expertise = [];
                    let educationLevel = [];
                    
                    try {
                        expertise = typeof tutor.expertise === 'string' ? 
                            JSON.parse(tutor.expertise.replace(/\\/g, '')) : 
                            (Array.isArray(tutor.expertise) ? tutor.expertise : []);
                    } catch (e) {
                        console.error("Error parsing expertise:", e);
                        expertise = [];
                    }
                    
                    try {
                        educationLevel = typeof tutor.education_level === 'string' ? 
                            JSON.parse(tutor.education_level.replace(/\\/g, '')) : 
                            (Array.isArray(tutor.education_level) ? tutor.education_level : []);
                    } catch (e) {
                        console.error("Error parsing education_level:", e);
                        educationLevel = [];
                    }

                    setFormData({
                        full_name: tutor.full_name || '',
                        email: tutor.email || '',
                        phone: tutor.phone || '',
                        qualification: tutor.qualification || '',
                        expertise: expertise,
                        subjects: tutor.subjects || '',
                        region: tutor.region || '',
                        experience: tutor.experience || '',
                        education_level: educationLevel,
                        availability: tutor.availability || 'part-time',
                        hourly_rate: tutor.hourly_rate || '',
                        status: tutor.status || 'pending',
                        verified: tutor.verified?.toString() || '0',
                        payment_status: tutor.payment_status || 'pending',
                        registration_paid: tutor.registration_paid?.toString() || '0'
                    });
                } else {
                    toast.error('Tutor not found');
                    navigate('/admin/tutors');
                }
            } catch (error) {
                console.error('Error fetching tutor:', error);
                toast.error('Failed to load tutor data');
                navigate('/admin/tutors');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTutorData();
        }
    }, [id, navigate]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            if (name === 'expertise' || name === 'education_level') {
                const currentArray = [...formData[name]];
                if (checked) {
                    setFormData(prev => ({
                        ...prev,
                        [name]: [...currentArray, value]
                    }));
                } else {
                    setFormData(prev => ({
                        ...prev,
                        [name]: currentArray.filter(item => item !== value)
                    }));
                }
            } else {
                setFormData(prev => ({
                    ...prev,
                    [name]: checked ? '1' : '0'
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.full_name || !formData.email) {
            toast.error('Full name and email are required');
            return;
        }

        try {
            setUpdating(true);
            
            const response = await axios.put(
                `https://pastq.eddcodesgh.com/qApi/admin/tutors/_tutors.php?id=${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506`
                    },
                    withCredentials: true
                }
            );

            if (response.data.status === 'success') {
                toast.success('Tutor updated successfully!');
                navigate('/admin/tutors');
            } else {
                toast.error(response.data.message || 'Failed to update tutor');
            }
        } catch (error) {
            console.error('Error updating tutor:', error);
            toast.error(error.response?.data?.message || 'Error updating tutor');
        } finally {
            setUpdating(false);
        }
    };

    // Reset form
    const handleReset = () => {
        if (window.confirm('Are you sure you want to reset all changes?')) {
            window.location.reload();
        }
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <Sidebar />
                <main id="main" className="main">
                    <div className="pagetitle">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h1>Loading Tutor...</h1>
                            </div>
                        </div>
                    </div>
                    <section className="section">
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3">Loading tutor information...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <Sidebar />
            <main id="main" className="main">
                <div className="pagetitle">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1>Edit Tutor: {formData.full_name}</h1>
                            <nav>
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/admin/tutors">Tutors</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Edit Tutor</li>
                                </ol>
                            </nav>
                        </div>
                        <div>
                            <Link to="/admin/tutors" className="btn btn-secondary">
                                <i className="bi bi-arrow-left"></i> Back to List
                            </Link>
                        </div>
                    </div>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">Edit Tutor Information</h5>
                                    
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            {/* Basic Information */}
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Full Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="full_name"
                                                    value={formData.full_name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Email *</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Phone Number</label>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="+233XXXXXXXXX"
                                                />
                                            </div>
                                            
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Region</label>
                                                <select
                                                    className="form-select"
                                                    name="region"
                                                    value={formData.region}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select Region</option>
                                                    {regionOptions.map(region => (
                                                        <option key={region} value={region}>{region}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Qualification</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="qualification"
                                                    value={formData.qualification}
                                                    onChange={handleChange}
                                                    placeholder="e.g., BSc. Computer Science"
                                                />
                                            </div>
                                            
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Subjects Taught</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="subjects"
                                                    value={formData.subjects}
                                                    onChange={handleChange}
                                                    placeholder="e.g., Mathematics, Physics, Chemistry"
                                                />
                                            </div>
                                            
                                            {/* Expertise (Checkboxes) */}
                                            <div className="col-12 mb-3">
                                                <label className="form-label">Expertise Levels</label>
                                                <div className="row">
                                                    {expertiseOptions.map(level => (
                                                        <div className="col-md-3" key={level}>
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    name="expertise"
                                                                    value={level}
                                                                    checked={formData.expertise.includes(level)}
                                                                    onChange={handleChange}
                                                                    id={`expertise-${level}`}
                                                                />
                                                                <label className="form-check-label" htmlFor={`expertise-${level}`}>
                                                                    {level}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Education Level (Checkboxes) */}
                                            <div className="col-12 mb-3">
                                                <label className="form-label">Education Level</label>
                                                <div className="row">
                                                    {educationOptions.map(level => (
                                                        <div className="col-md-3" key={level}>
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    name="education_level"
                                                                    value={level}
                                                                    checked={formData.education_level.includes(level)}
                                                                    onChange={handleChange}
                                                                    id={`education-${level}`}
                                                                />
                                                                <label className="form-check-label" htmlFor={`education-${level}`}>
                                                                    {level}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Experience</label>
                                                <select
                                                    className="form-select"
                                                    name="experience"
                                                    value={formData.experience}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select Experience</option>
                                                    <option value="0-1">0-1 years</option>
                                                    <option value="1-3">1-3 years</option>
                                                    <option value="3-5">3-5 years</option>
                                                    <option value="5-10">5-10 years</option>
                                                    <option value="10+">10+ years</option>
                                                </select>
                                            </div>
                                            
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Availability</label>
                                                <select
                                                    className="form-select"
                                                    name="availability"
                                                    value={formData.availability}
                                                    onChange={handleChange}
                                                >
                                                    <option value="full-time">Full Time</option>
                                                    <option value="part-time">Part Time</option>
                                                    <option value="weekends">Weekends Only</option>
                                                    <option value="evenings">Evenings Only</option>
                                                    <option value="flexible">Flexible</option>
                                                </select>
                                            </div>
                                            
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Hourly Rate (GHS)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="hourly_rate"
                                                    value={formData.hourly_rate}
                                                    onChange={handleChange}
                                                    step="0.01"
                                                    min="0"
                                                />
                                            </div>
                                            
                                            {/* Status Fields */}
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Account Status</label>
                                                <select
                                                    className="form-select"
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleChange}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="active">Active</option>
                                                    <option value="suspended">Suspended</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                            </div>
                                            
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Verification Status</label>
                                                <select
                                                    className="form-select"
                                                    name="verified"
                                                    value={formData.verified}
                                                    onChange={handleChange}
                                                >
                                                    <option value="0">Not Verified</option>
                                                    <option value="1">Verified</option>
                                                </select>
                                            </div>
                                            
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Payment Status</label>
                                                <select
                                                    className="form-select"
                                                    name="payment_status"
                                                    value={formData.payment_status}
                                                    onChange={handleChange}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="paid">Paid</option>
                                                    <option value="failed">Failed</option>
                                                </select>
                                            </div>
                                            
                                            {/* Registration Paid Checkbox */}
                                            <div className="col-12 mb-3">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name="registration_paid"
                                                        checked={formData.registration_paid === '1'}
                                                        onChange={handleChange}
                                                        id="registration_paid"
                                                    />
                                                    <label className="form-check-label" htmlFor="registration_paid">
                                                        Registration Fee Paid
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-4">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary"
                                                disabled={updating}
                                            >
                                                {updating ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-save me-2"></i>
                                                        Update Tutor
                                                    </>
                                                )}
                                            </button>
                                            
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary ms-2"
                                                onClick={handleReset}
                                                disabled={updating}
                                            >
                                                <i className="bi bi-arrow-clockwise me-2"></i>
                                                Reset Changes
                                            </button>
                                            
                                            <Link to="/admin/tutors" className="btn btn-outline-secondary ms-2">
                                                <i className="bi bi-x-circle me-2"></i>
                                                Cancel
                                            </Link>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            
                            {/* Tutor Info Card */}
                            <div className="card mt-4">
                                <div className="card-body">
                                    <h5 className="card-title">Tutor Information</h5>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p><strong>Tutor ID:</strong> {id}</p>
                                            <p><strong>Created:</strong> {new Date().toLocaleDateString()}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p><strong>Current Status:</strong> 
                                                <span className={`badge ms-2 bg-${formData.status === 'active' ? 'success' : 'warning'}`}>
                                                    {formData.status}
                                                </span>
                                            </p>
                                            <p><strong>Verification:</strong> 
                                                <span className={`badge ms-2 bg-${formData.verified === '1' ? 'success' : 'secondary'}`}>
                                                    {formData.verified === '1' ? 'Verified' : 'Not Verified'}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default UpdateTutors;